import { Product, ProductTag } from '@interfaces/models/product';
import { useQuery } from '@tanstack/react-query';
import { ProductPdp } from '@interfaces/models/pdp/productPdp';
import { ProductPdpPageQueryKeys } from '@enums/react-query-keys';
import { AvailableCountryISOCodes } from '@interfaces/models/country';
import { PdpModel } from '@interfaces/models/pdp/pdpModel';
import { PdpBrand } from '@interfaces/models/pdp/pdpBrand';
import { PdpUniverse } from '@interfaces/models/pdp/pdpUniverse';
import { PdpCategory } from '@interfaces/models/pdp/pdpCategory';
import { PdpSubcategory } from '@interfaces/models/pdp/pdpSubcategory';
import { PdpMeshLink } from '@interfaces/models/pdp/pdpMeshLink';
import { PdpAttribute } from '@interfaces/models/pdp/pdpAttribute';
import { PdpDimension } from '@interfaces/models/pdp/pdpDimension';
import { PdpSeller } from '@interfaces/models/pdp/pdpSeller';
import UserBadge from '@enums/userBadge';
import { SupportedLanguages } from '@interfaces/models/language';
import { UserTransactionRole } from '@interfaces/models/userTransactionRole';
import { isSellerOnVacation as isProductSellerOnVacation, isShippable } from '@helpers/utils/products';
import { PdpWebChannel } from '@interfaces/models/pdp/pdpWebChannel';
import useDirectShippingEligibilityQuery from '@hooks/header/use-direct-shipping-eligibility-query';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useUser from '@hooks/user/use-user';

export type ProductQueryResponse = {
  product: Product;
  isProductDirectShippable: boolean;
  isProductBuyable: boolean;
  isSellerOnVacation: boolean;
  userTransactionRole: UserTransactionRole;
  isProductDirectShippableForCurrentUser: boolean;
};

export const getMeshLinksObject = (meshLinks: PdpMeshLink) => {
  const { name, path } = meshLinks;
  return {
    localizedName: name,
    name: name,
    url: {
      path: path,
    },
  };
};

export const getMeshLinks = (meshLinks: PdpWebChannel['meshLinks']) => {
  return {
    ...(meshLinks?.topCategory && { topCategory: getMeshLinksObject(meshLinks?.topCategory) }),
    ...(meshLinks?.category && { category: getMeshLinksObject(meshLinks?.category) }),
    ...(meshLinks?.categoryBrand && { categoryBrand: getMeshLinksObject(meshLinks?.categoryBrand) }),
    ...(meshLinks?.categoryBrandModel && { categoryBrandModel: getMeshLinksObject(meshLinks?.categoryBrandModel) }),
    ...(meshLinks?.categoryBrandModelMaterial && {
      categoryBrandModelMaterial: getMeshLinksObject(meshLinks?.categoryBrandModelMaterial),
    }),
    ...(meshLinks?.categoryBrandModelMaterialColor && {
      categoryBrandModelMaterialColor: getMeshLinksObject(meshLinks?.categoryBrandModelMaterialColor),
    }),
  };
};

// TODO: Type the return
export const getPdpMapping = (attribute: PdpModel | PdpBrand | PdpUniverse | PdpCategory | PdpSubcategory) => {
  if (attribute) {
    const { nameLocalized, nameOrigin, parent } = attribute;
    return {
      ...attribute,
      name: nameOrigin,
      localizedName: nameLocalized,
      ...(parent && {
        parent: getPdpMapping(parent),
      }),
    };
  }
};

export const getSellerMapping = (seller: PdpSeller) => {
  return {
    ...seller,
    statistics: {
      productsSold: seller?.statistics?.productSoldCount,
    },
    internalAccount: seller?.isInternalAccount,
    badges: seller?.badges.map((badge) => badge.name) as UserBadge[],
    vacation: seller?.isOnVacation,
    segment: seller?.segment as 'C2C' | 'VIP' | 'B2C',
    // to check with Backend team to retuen language as Object
    language: {
      name: seller?.language,
      localizedName: seller?.language,
      code: seller?.language as SupportedLanguages,
    },
    sellerRating: {
      badge: seller?.rating?.badge,
      goals: {
        tags: seller?.rating?.goal?.reduce((obj, item) => Object.assign(obj, { [item.name]: item.value }), {}),
      },
    },
  };
};

export const getPdpDynamicAttribute = (attribute: PdpAttribute) => {
  const { valueType, group, options, treeOptions, sizeLabel, dimensions } = attribute;
  if (valueType === 'options') {
    return {
      name: options[0].nameOrigin,
      localizedName: options[0].nameLocalized,
      ...(group === 'condition' && { description: options[0].nameLocalized }),
    };
  }
  if (valueType === 'treeOptions') {
    return {
      name: treeOptions[0].nameOrigin,
      localizedName: treeOptions[0].nameLocalized,
      ...(treeOptions[0].parentNameOrigin && {
        name: treeOptions[0].parentNameOrigin,
        localizedName: treeOptions[0].parentNameLocalized,
      }),
    };
  }
  if (valueType === 'sizeLabel') {
    return {
      size: sizeLabel,
    };
  }
  if (valueType === 'dimensions') {
    return dimensions?.map((dimension: PdpDimension) => {
      return {
        name: dimension.nameOrigin,
        localizedName: dimension.nameLocalized,
        value: dimension.value,
      };
    });
  }
  return null;
};

// to map the group from PROCS to current product fields (If backend add/remove any group, they will inform front)
// More Info: https://vestiairecollective.atlassian.net/wiki/spaces/TAX/pages/292782222/Product+Details+Page+PDP+Integration#Group
const newProductAttributeToCurrentProductKeyMap = new Map([
  ['colour', 'color'],
  ['watch_mechanism', 'watchMechanism'],
  ['watch_bracelet_material', 'watchStrapMaterial'],
  ['dimension', 'measurements'],
  ['packaging', 'packagings'],
  ['pattern', 'pattern'],
  ['material', 'material'],
  ['gender_wear', 'gender_wear'],
  ['condition', 'condition'],
  ['serial_number', 'serial_number'],
  ['custom_brand', 'custom_brand'],
  ['price_of_purchase', 'price_of_purchase'],
  ['sku', 'sku'],
  ['size', 'size'],
]);

export const getMappedProduct = (product: Product | ProductPdp) => {
  if (!product) {
    return null;
  }
  // Check if product is "old" product
  if (product.type === 'product') {
    return product;
  }

  const pdpProduct = product as ProductPdp;
  return {
    id: pdpProduct?.id,
    type: pdpProduct?.type,
    name: pdpProduct?.productBrowsingCatalog?.product?.name,
    price: pdpProduct?.pricing?.price,
    regularPrice: pdpProduct?.pricing?.regularPrice,
    sellerPrice: pdpProduct?.pricing?.sellerPrice,
    // to check
    dutyAndTax: null,
    description: pdpProduct?.categorization?.descriptionOrigin,
    localizedDescription: pdpProduct?.categorization?.descriptionLocalized,
    likeCount: pdpProduct?.productBrowsingCatalog?.discoveryEnhancements?.likeCount,
    path: pdpProduct?.productBrowsingCatalog?.webChannel?.productPath,
    sold: pdpProduct?.inventory?.isSold,
    negotiable: pdpProduct?.pricing?.isNegotiable,
    inStock: pdpProduct?.inventory?.isInStock,
    isLocal: pdpProduct?.inventory?.delivery?.isExpeditionToSameCountry,
    creationDate: pdpProduct?.deposit?.dateCreated,
    soldDate: pdpProduct?.inventory?.dateSold,
    pictures: pdpProduct?.deposit?.pictures,
    ...Object.fromEntries(
      (pdpProduct?.categorization?.attributes ?? [])
        .filter((attribute) => attribute.valueType !== 'tag')
        .map((attribute: PdpAttribute) => {
          return [newProductAttributeToCurrentProductKeyMap.get(attribute.group), getPdpDynamicAttribute(attribute)];
        }),
    ),
    tags: (pdpProduct?.categorization?.attributes ?? [])
      .filter((attribute) => attribute.valueType === 'tag' && attribute.tag)
      .map((attr) => attr.group) as ProductTag[],
    brand: getPdpMapping(pdpProduct?.categorization?.brand),
    model: getPdpMapping(pdpProduct?.categorization?.model),
    universe: getPdpMapping(pdpProduct?.productBrowsingCatalog?.navigation?.universe),
    category: getPdpMapping(pdpProduct?.productBrowsingCatalog?.navigation?.category),
    subcategory: getPdpMapping(pdpProduct?.productBrowsingCatalog?.navigation?.subcategory),
    meshLinks: getMeshLinks(pdpProduct?.productBrowsingCatalog?.webChannel?.meshLinks),
    buyersWhoHaveActiveNegotiation: pdpProduct?.pricing?.buyersWithActiveNegotiations,
    installment: pdpProduct?.pricing?.installment,
    receipt: null,
    available: pdpProduct?.inventory?.delivery?.isAvailableInBuyersCountry,
    alternateVersions: [],
    metadata: pdpProduct?.productBrowsingCatalog?.webChannel?.metadata,
    consignment: pdpProduct?.deposit?.isConsignment,
    buyerFees: [
      {
        cost: {
          cents: pdpProduct?.pricing?.buyerFees[0]?.costCents,
          formatted: pdpProduct?.pricing.buyerFees[0]?.costFormatted,
          currency: null,
        },
      },
    ],
    isoCountry: pdpProduct?.inventory?.delivery?.expeditionCountryISO as AvailableCountryISOCodes,
    flags: pdpProduct?.productBrowsingCatalog?.discoveryEnhancements?.tags,
    seller: getSellerMapping(pdpProduct?.seller),
    originalDescription: pdpProduct?.categorization?.descriptionOrigin,
  };
};

/**
 * For backwards compatibility, useProduct supports both pdpProduct and 'old' product. When passing in a product,
 * it will ignore the query for the new product and vice-versa. Support for old product will eventually be removed.
 */
const useProductQuery = (initialProduct?: Product) => {
  const { productShipping } = useProductShippingQuery();
  const { isUserDirectShippingEligible } = useDirectShippingEligibilityQuery();
  const { user } = useUser();

  const { data: product } = useQuery<Product | ProductPdp, Error, Product>({
    queryKey: [ProductPdpPageQueryKeys.GET_PDP_PRODUCT],
    staleTime: Infinity,
    initialData: initialProduct,
    select: (product) => getMappedProduct(initialProduct || product),
  });

  const isProductDirectShippable: boolean = !!product?.flags?.includes('direct-shipping');
  const userTransactionRole: UserTransactionRole =
    !user || !product ? 'buyer' : user.id === product?.seller?.id ? 'seller' : 'buyer';
  const isProductBuyable: boolean = !product
    ? false
    : isShippable(productShipping) && !product?.sold && !product?.reserved;
  const isProductDirectShippableForCurrentUser: boolean = isUserDirectShippingEligible && isProductDirectShippable;
  const isSellerOnVacation: boolean = !product ? false : isProductSellerOnVacation(product);

  return {
    product,
    isProductDirectShippable,
    isProductBuyable,
    isSellerOnVacation,
    userTransactionRole,
    isProductDirectShippableForCurrentUser,
  };
};

export default useProductQuery;
